import { createFileRoute } from "@tanstack/react-router";
import { NanaeHeader, NanaeFooter } from "@/components/qv3/NanaeShared";
import { socialMeta, pageJsonLd } from "@/lib/seo-meta";

export const Route = createFileRoute("/legal")({
  head: () => ({
    meta: [
      { title: "Mentions légales — Hōjin" },
      {
        name: "description",
        content:
          "Mentions légales de Hōjin : éditeur UDITEK SAS, hébergement, nature du service, données personnelles et propriété intellectuelle.",
      },
      ...socialMeta({
        title: "Mentions légales — Hōjin",
        description: "Mentions légales de Hōjin : éditeur UDITEK SAS, hébergement, nature du service, données personnelles et propriété intellectuelle.",
        url: "https://hojin.ai/legal",
        image: "conseil",
        card: "summary",
      }),
    ],
    links: [{ rel: "canonical", href: "https://hojin.ai/legal" }],
    scripts: pageJsonLd({
      path: "/legal",
      name: "Mentions légales — Hōjin",
      description: "Mentions légales de Hōjin : éditeur UDITEK SAS, hébergement, nature du service, données personnelles et propriété intellectuelle.",
      type: "WebPage",
      image: "conseil",
      breadcrumb: [{ name: "Mentions légales", path: "/legal" }],
    }),
  }),
  component: LegalPage,
});

type Section = { title: string; body: React.ReactNode };

const SECTIONS: Section[] = [
  {
    title: "Éditeur du site",
    body: (
      <>
        <p>
          Hōjin est un service édité par <strong>UDITEK SAS</strong>, société par
          actions simplifiée au capital de 4 000 €, immatriculée au RCS de Paris
          sous le numéro <strong>804 931 327</strong>, dont le siège social est
          situé à Paris.
        </p>
        <p>
          Directeur de la publication : le représentant légal d'UDITEK SAS.
        </p>
        <p>
          Contact :{" "}
          <a
            href="mailto:contact@hojin.ai"
            style={{ color: "var(--accent-cramoisi)", textDecoration: "underline" }}
          >
            contact@hojin.ai
          </a>
        </p>
      </>
    ),
  },
  {
    title: "Hébergement",
    body: (
      <p>
        Application hébergée par Railway Corp. et Supabase Inc., données
        hébergées dans l'Union européenne pour les données utilisateurs.
      </p>
    ),
  },
  {
    title: "Nature du service",
    body: (
      <p>
        Hōjin est un outil d'aide à la décision fondé sur l'intelligence
        artificielle. Les analyses, verdicts et mémos produits sont des
        éclairages destinés à nourrir la réflexion du dirigeant : ils ne
        constituent ni un conseil juridique, ni un conseil comptable, ni un
        conseil en investissement, et ne se substituent en aucun cas à l'avis
        d'un professionnel réglementé (avocat, expert-comptable, notaire,
        conseiller financier).
      </p>
    ),
  },
  {
    title: "Données personnelles",
    body: (
      <p>
        Les données sont traitées conformément au RGPD. Elles ne sont ni
        revendues ni utilisées pour entraîner des modèles d'intelligence
        artificielle. Chaque utilisateur peut demander la suppression de ses
        données :{" "}
        <a
          href="mailto:contact@hojin.ai"
          style={{ color: "var(--accent-cramoisi)", textDecoration: "underline" }}
        >
          contact@hojin.ai
        </a>
        .
      </p>
    ),
  },
  {
    title: "Propriété intellectuelle",
    body: (
      <p>
        L'ensemble des éléments du site (marque, textes, interface) est la
        propriété d'UDITEK SAS. Les mémos et livrables générés pour un
        utilisateur lui appartiennent.
      </p>
    ),
  },
];

export function LegalPage() {
  return (
    <div className="nanae-page" style={{ background: "var(--ivory)", minHeight: "100vh" }}>
      <NanaeHeader title="Mentions légales" variant="public" />
      <section
        aria-label="Mentions légales"
        data-rp="legal-page"
        style={{
          maxWidth: 720,
          margin: "0 auto",
          padding: "64px 32px 80px",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 20,
          }}
        >
          <span
            aria-hidden
            style={{ width: 24, height: 0.5, background: "var(--accent-cramoisi)", display: "inline-block" }}
          />
          <span
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: 10,
              color: "var(--gold-deep)",
              letterSpacing: "3px",
              textTransform: "uppercase",
            }}
          >
            Mentions légales · Volume MMXXVI
          </span>
        </div>
        <h1
          data-rp="legal-h1"
          style={{
            fontFamily: '"Anton", sans-serif',
            fontWeight: 400,
            fontSize: 64,
            color: "var(--charcoal)",
            letterSpacing: "-1.5px",
            textTransform: "uppercase",
            lineHeight: 0.95,
            margin: "0 0 48px",
          }}
        >
          Mentions légales
        </h1>

        <div>
          {SECTIONS.map((s) => (
            <section key={s.title} style={{ marginBottom: 8 }}>
              <h2
                data-rp="legal-section-h2"
                style={{
                  fontFamily: '"Anton", sans-serif',
                  fontWeight: 400,
                  fontSize: 28,
                  color: "var(--charcoal)",
                  letterSpacing: "-0.5px",
                  textTransform: "uppercase",
                  marginTop: 32,
                  marginBottom: 12,
                }}
              >
                {s.title}
              </h2>
              <div
                style={{
                  fontFamily: '"Playfair Display", Georgia, serif',
                  fontSize: 15,
                  color: "var(--charcoal)",
                  lineHeight: 1.65,
                }}
              >
                {s.body}
              </div>
            </section>
          ))}
        </div>
      </section>
      <NanaeFooter variant="full" />
    </div>
  );
}
